import {
  AudienceIcon,
  ClickIcon,
  CubeIcon,
  FrameStackIcon,
  GlobeIcon,
  type IconBoxProps,
  JourneyIcon,
  LightningBoltIcon,
  MailClickIcon,
  MergeIcon,
  MobilePhoneIcon,
  PlusIcon,
  SmsTappedIcon,
  TraitIcon,
  TraitTemplateIcon,
} from "@hightouch/htui";
import { isPresent } from "ts-extras";

import { isRealtimeEventModel } from "src/utils/event-models";
import { ChannelType } from "src/types/cep";
import {
  type FilterRestrictions,
  isDenied,
} from "src/components/explore/restrictions";
import type { RealtimeConditionScope } from "src/components/explore/context/hooks";

export const PLACEMENT_INFLECTION_POINT = 800;
export const ROW_HEIGHT = 10; // 40px

export enum FilterOption {
  All = "ALL",
  Property = "Property",
  Relation = "Relation",
  Event = "Event",
  EventProperty = "Event Property",
  RealtimeEvent = "Realtime Event",
  Audience = "Audience",
  Trait = "Trait",
  TraitTemplate = "Trait Template",
  InlineTrait = "Custom Trait",
  Journey = "Journey",
  MessageEvent = "Message Event",
  JourneyMemberState = "Member state",
}

export enum MetadataType {
  MergedColumn = "MergedColumn",
  Event = "Event",
  Relation = "Relation",
}

export type FilterableSection = {
  type: FilterOption;
  label: string;
  shade: IconBoxProps["color"];
  icon: IconBoxProps["icon"];
  iconVariant?: IconBoxProps["variant"];
  tip?: string;
  separator?: boolean;
  heading?: string;
  /**
   * If this is the only section with options, the filter popover
   * will automatically reduce to a select. This is the placeholder for that select.
   */
  placeholder?: string;

  // temporary, should be removed when refactoring the section configuration
  overrideColumnIcon?: boolean;
};

// TODO(nolan): can we just change all these to be the component instead of the JSX element?
export const GlobalColumn: FilterableSection = {
  label: "All filters",
  icon: <GlobeIcon />,
  shade: "gray",
  type: FilterOption.All,
  separator: true,
};

export const ParentIcon = CubeIcon;
export const RelationIcon = FrameStackIcon;
export const EventIcon = ClickIcon;
export const RealtimeEventIcon = LightningBoltIcon;

export const PropertyColumn: FilterableSection = {
  label: "Properties",
  icon: <ParentIcon />,
  shade: "indigo",
  type: FilterOption.Property,
  tip: "A person's attributes, e.g. Location",
  placeholder: "Select a property…",
};

export const PropertyColumnForEvents: FilterableSection = {
  label: "Event properties",
  icon: <EventIcon />,
  shade: "teal",
  iconVariant: "inverted",
  type: FilterOption.Property,
  tip: "Attributes on the event",
  placeholder: "Select an event property…",
  overrideColumnIcon: true,
  separator: true,
};

export const PropertyColumnForRelations: FilterableSection = {
  label: "Relation properties",
  icon: <RelationIcon />,
  shade: "green",
  iconVariant: "inverted",
  type: FilterOption.Property,
  tip: "Attributes on the related model",
  placeholder: "Select a related model property…",
  overrideColumnIcon: true,
};

export const PropertyColumnForRealtimeEvents: FilterableSection = {
  label: "Realtime event properties",
  icon: <LightningBoltIcon />,
  shade: "teal",
  iconVariant: "inverted",
  type: FilterOption.Property,
  overrideColumnIcon: true,
};

export const RelationColumn: FilterableSection = {
  label: "Relations",
  icon: <RelationIcon />,
  shade: "green",
  type: FilterOption.Relation,
  placeholder: "Select a related model…",
  tip: "Properties of related models",
};

const RelationColumnWithHeading: FilterableSection = {
  ...RelationColumn,
  heading: "Additional filters",
};

export const EventColumn: FilterableSection = {
  label: "Events",
  icon: <EventIcon />,
  shade: "teal",
  type: FilterOption.Event,
  placeholder: "Select an event…",
  tip: "An event performed, e.g. AddToCart",
};

export const TraitColumn: FilterableSection = {
  label: "Traits",
  icon: <TraitIcon />,
  shade: "yellow",
  type: FilterOption.Trait,
  tip: "Custom calculated attributes, e.g. LTV",
  placeholder: "Select a trait…",
  separator: true,
};

export const AudienceColumn: FilterableSection = {
  label: "Audiences",
  icon: <AudienceIcon />,
  shade: "blue",
  type: FilterOption.Audience,
  placeholder: "Select an audience…",
  tip: "Include or exclude other audiences",
};

export const JourneyMemberStateColumn: FilterableSection = {
  label: "Member state",
  icon: <JourneyIcon />,
  shade: "gray",
  type: FilterOption.JourneyMemberState,
  tip: "Compare when this member entered the journey",
  placeholder: "Select member state…",
};

export const JourneyColumn: FilterableSection = {
  label: "Journeys",
  icon: <JourneyIcon />,
  shade: "pink",
  type: FilterOption.Journey,
  placeholder: "Select a journey…",
  tip: "Filter by whether they’re in a journey",
};

export const EventPropertyColumn: FilterableSection = {
  label: "Event properties",
  icon: <ClickIcon />,
  shade: "teal",
  placeholder: "Select an event property…",
  type: FilterOption.EventProperty,
};

export const RealtimeEventColumn: FilterableSection = {
  label: "Realtime events",
  icon: <LightningBoltIcon />,
  shade: "teal",
  type: FilterOption.RealtimeEvent,
};

/**
 * Picks the filter section (and therefore the icon/shade) for a model that is
 * the target of a relationship — e.g. when selecting the related/event model
 * for a trait. Realtime event models get the lightning-bolt icon to match how
 * realtime events are surfaced elsewhere (query builder, schema, audiences).
 */
export const getRelatedModelSection = (toModel: {
  event?: { event_model_event_sources?: readonly unknown[] | null } | null;
  is_schema?: boolean | null;
  primary_key?: string | null;
}): FilterableSection => {
  if (toModel.event) {
    return isRealtimeEventModel(toModel.event)
      ? RealtimeEventColumn
      : EventColumn;
  }
  if (toModel.is_schema && toModel.primary_key) {
    return PropertyColumn;
  }
  return RelationColumn;
};

export const MessageEventsColumn: FilterableSection = {
  label: "Message events",
  icon: <MailClickIcon />,
  shade: "orange",
  type: FilterOption.MessageEvent,
};

export const MESSAGE_EVENT_CHANNEL_ICON_OVERRIDES: Record<
  ChannelType,
  { icon: JSX.Element; color: IconBoxProps["color"] }
> = {
  [ChannelType.Email]: { icon: <MailClickIcon />, color: "orange" },
  [ChannelType.Sms]: { icon: <SmsTappedIcon />, color: "orange" },
  [ChannelType.Whatsapp]: { icon: <SmsTappedIcon />, color: "orange" },
  [ChannelType.Push]: { icon: <MobilePhoneIcon />, color: "orange" },
  [ChannelType.InApp]: { icon: <MobilePhoneIcon />, color: "orange" },
};

export const TraitTemplateColumn: FilterableSection = {
  ...TraitColumn,
  icon: <TraitTemplateIcon />,
  type: FilterOption.TraitTemplate,
};

export const InlineTraitColumn: FilterableSection = {
  label: "Custom traits",
  icon: <PlusIcon />,
  shade: "yellow",
  type: FilterOption.InlineTrait,
};

export const MetadataIcons = {
  [MetadataType.MergedColumn]: <MergeIcon />,
  [MetadataType.Event]: EventColumn.icon,
  [MetadataType.Relation]: RelationColumn.icon,
};

/**
 * Base sections for the root query builder.
 */
export const getRootFilterSections: (opts: {
  showJourneys?: boolean;
  restrictions?: FilterRestrictions;
  realtimeConditionScope?: RealtimeConditionScope;
  propertiesOnly?: boolean;
  enableJourneyMemberState?: boolean;
}) => FilterableSection[] = ({
  showJourneys,
  restrictions,
  realtimeConditionScope,
  propertiesOnly,
  enableJourneyMemberState,
}) => {
  if (propertiesOnly) {
    return [GlobalColumn, PropertyColumn];
  }
  const showTraits = !isDenied(restrictions, "traits");
  const showRelations = !isDenied(restrictions, "related-models");
  const isRealtimeAudience = realtimeConditionScope === "audience";

  // A realtime journey's conditions can use realtime traits, realtime audiences, and journey member state.
  // Warehouse columns, related-model counts, events and journey
  // membership all compile to conditions the realtime evaluator cannot run, so
  // they are not offered rather than rejected at save.
  if (realtimeConditionScope === "journey-criteria") {
    return [
      GlobalColumn,
      showTraits ? TraitColumn : undefined,
      isDenied(restrictions, "audiences") ? undefined : AudienceColumn,
      enableJourneyMemberState ? JourneyMemberStateColumn : undefined,
    ].filter(isPresent);
  }
  return [
    GlobalColumn,
    PropertyColumn,
    showTraits ? TraitColumn : undefined,
    showRelations ? RelationColumn : undefined,
    // The separator only divides events from the trait/relation sections.
    showTraits || showRelations
      ? { ...EventColumn, separator: true }
      : EventColumn,
    MessageEventsColumn,
    isRealtimeAudience || isDenied(restrictions, "audiences")
      ? undefined
      : AudienceColumn,
    showJourneys && !isRealtimeAudience && !isDenied(restrictions, "journeys")
      ? JourneyColumn
      : undefined,
  ].filter(isPresent);
};

const oldRelationSubconditionFilterableSections: FilterableSection[] = [
  PropertyColumnForRelations,
  TraitColumn,
];

const oldEventSubconditionFilterableSections: FilterableSection[] = [
  PropertyColumnForEvents,
  TraitColumn,
];

const realtimeRelationSubconditionFilterableSections: FilterableSection[] = [
  PropertyColumnForRelations,
];

const realtimeEventSubconditionFilterableSections: FilterableSection[] = [
  PropertyColumnForRealtimeEvents,
];

const relationSubconditionFilterableSectionsWithNestedFiltering: FilterableSection[] =
  [
    GlobalColumn,
    { ...PropertyColumnForRelations, separator: true },
    RelationColumnWithHeading,
    EventColumn,
    TraitColumn,
  ];

const relationSubconditionParentWithNestedFiltering: FilterableSection[] = [
  GlobalColumn,
  PropertyColumnForRelations,
  TraitColumn,
  RelationColumnWithHeading,
  EventColumn,
];

const eventSubconditionFilterableSectionsWithNestedFiltering: FilterableSection[] =
  [
    GlobalColumn,
    PropertyColumnForEvents,
    RelationColumnWithHeading,
    EventColumn,
    TraitColumn,
  ];

/**
 * Sections for nested filters inside 'NumberOfFilter'.
 * We only want to show properties of the related model.
 */
export const getRelationSubconditionFilterableSections = (opts: {
  isParentModel?: boolean;
  enableNestedFiltering?: boolean;
  isRealtimeAudience: boolean;
}): FilterableSection[] => {
  if (opts.isRealtimeAudience) {
    return realtimeRelationSubconditionFilterableSections;
  }

  if (!opts.enableNestedFiltering) {
    return oldRelationSubconditionFilterableSections;
  }

  if (opts.isParentModel) {
    return relationSubconditionParentWithNestedFiltering;
  }

  return relationSubconditionFilterableSectionsWithNestedFiltering;
};

/**
 * Sections for nested filters inside 'EventFilter'.
 * We only want to show properties of the selected event.
 */
export const getEventSubconditionFilterableSections = (opts: {
  enableNestedFiltering?: boolean;
  isRealtimeAudience: boolean;
}): FilterableSection[] =>
  opts.isRealtimeAudience
    ? realtimeEventSubconditionFilterableSections
    : opts.enableNestedFiltering
      ? eventSubconditionFilterableSectionsWithNestedFiltering
      : oldEventSubconditionFilterableSections;

export const getFunnelSubconditionFilterableSections =
  (): FilterableSection[] => oldEventSubconditionFilterableSections;

export const TRAIT_SUBCONDITION_FILTERABLE_SECTIONS: FilterableSection[] = [
  PropertyColumn,
];

export const PADDING = 2;
export const GAP = 2;
export const MAX_INPUT_WIDTH_PX = 312;

export const getMaxPopoverHeightPx = (numTabs: number) => {
  // This effectively sets a minimum height
  const tabsForHeight = Math.max(numTabs, 6);
  return tabsForHeight * 56 + 4 * 2;
};

export const METADATA_SECTION_WIDTH_PX = [208, 224, 256, 256, 288];
